/**
 * STUNDENKONTINGENT — Eingabefeld neben dem BestandteileEditor
 * (docs/stundenkontingent-vertrag.md, Paket E).
 *
 * Eingebettet in: Katalog (Vorlage je Katalogposition und neue Position),
 * Projektanlage, Nachtrag, Angebot (intern). Ob es erscheint, entscheidet der
 * Aufrufer — nur an einer Position MIT Bestandteilen (S1). Wer es aendern
 * darf, ebenfalls (`deaktiviert`); gesperrt zeigt es den Wert nur lesend.
 *
 * Kontrolliert: `wert` kommt von aussen, `onChange` meldet jede Eingabe als
 * gueltigen Wert oder `undefined` (leer = kein Kontingent; ungueltig = auch
 * kein Wert, mit Fehlertext — ein ungueltiger Wert wird nie gesendet). Der
 * Text des Feldes ist eigener Zustand, damit Zwischenstaende wie „62," beim
 * Tippen stehen bleiben.
 *
 * Bewusst KEIN Preis, keine Menge — und der Hinweis „intern" steht immer
 * dabei: das Kontingent erscheint nie auf Rechnung, Leistungsnachweis oder
 * Angebot (S8).
 */
import React, { useId, useState } from 'react';
import { Clock } from 'lucide-react';
import { gueltigesKontingent } from '../../services/bestandteile/kontingentKopie';
import { kontingentAlsText, leseKontingentEingabe } from './bestandteileEditorLogic';

export interface StundenKontingentFeldProps {
    /** Das Kontingent der Position; null/undefined = keins. */
    wert: number | null | undefined;
    /** Gueltiger Wert oder `undefined` (leer bzw. ungueltig). */
    onChange: (wert: number | undefined) => void;
    /** Nur lesend (kein Admin, gesperrtes Angebot, bestehende Position). */
    deaktiviert?: boolean;
    /** Warum gesperrt — steht dann unter dem Feld. */
    sperrHinweis?: string;
    /** Meldet den Fehlertext (oder null) bei jeder Eingabe — z. B. um „Speichern" zu sperren. */
    onFehler?: (fehler: string | null) => void;
}

/** Zwischenstand beim Tippen („62," / „62.") — der Fehler wartet bis zum Verlassen des Feldes. */
const istZwischenstand = (text: string): boolean => /[.,]\s*$/.test(text);

export const StundenKontingentFeld: React.FC<StundenKontingentFeldProps> = ({ wert, onChange, deaktiviert, sperrHinweis, onFehler }) => {
    const feldId = useId();
    const hinweisId = `${feldId}-hinweis`;
    const fehlerId = `${feldId}-fehler`;
    const [text, setText] = useState(() => kontingentAlsText(wert));
    const [verlassen, setVerlassen] = useState(false);
    const extern = gueltigesKontingent(wert);

    // Von aussen geaendert (Vorlage uebernommen, Bestandteile entfernt, andere
    // Zeile): den Text nachziehen — aber nie eine laufende Eingabe
    // ueberschreiben, die denselben Wert meint („62,50" bleibt „62,50").
    // Waehrend des Renderns statt im Effect (React: „Informationen aus dem
    // vorigen Render speichern") — kein zweiter Render mit veraltetem Text.
    const [zuletztExtern, setZuletztExtern] = useState(extern);
    if (extern !== zuletztExtern) {
        setZuletztExtern(extern);
        if (leseKontingentEingabe(text).wert !== extern) setText(kontingentAlsText(extern));
    }

    const { fehler } = leseKontingentEingabe(text);
    const zeigeFehler = !deaktiviert && !!fehler && (verlassen || !istZwischenstand(text));

    return (
        <div className="w-full">
            <label htmlFor={feldId} className="flex items-center gap-1.5 text-[11px] font-bold uppercase text-slate-500 mb-1">
                <Clock className="w-3.5 h-3.5" aria-hidden="true" />
                Stundenkontingent (intern)
            </label>
            <div className="flex items-center gap-2">
                <input
                    id={feldId}
                    type="text"
                    inputMode="decimal"
                    autoComplete="off"
                    value={text}
                    disabled={deaktiviert}
                    placeholder={deaktiviert ? 'kein Kontingent' : 'z. B. 100'}
                    aria-invalid={zeigeFehler || undefined}
                    aria-describedby={zeigeFehler ? `${fehlerId} ${hinweisId}` : hinweisId}
                    onChange={(e) => {
                        const roh = e.target.value;
                        setText(roh);
                        const r = leseKontingentEingabe(roh);
                        onFehler?.(r.fehler);
                        if (r.wert !== extern) onChange(r.wert);
                    }}
                    onBlur={() => setVerlassen(true)}
                    className={`w-full min-w-0 min-h-11 sm:min-h-9 px-3 py-1.5 bg-white border rounded-lg text-sm text-slate-700 outline-none focus:ring-2 disabled:bg-slate-50 disabled:text-slate-500 disabled:cursor-not-allowed ${zeigeFehler ? 'border-red-300 focus:border-red-400 focus:ring-red-500/15' : 'border-slate-200 focus:border-blue-400 focus:ring-blue-500/15'}`}
                />
                <span className="shrink-0 text-sm font-semibold text-slate-500" aria-hidden="true">h</span>
            </div>
            {zeigeFehler && (
                <p id={fehlerId} role="alert" className="mt-1 text-[11px] font-semibold text-red-700">{fehler}</p>
            )}
            <p id={hinweisId} className="mt-1 text-[11px] text-slate-500">
                Intern — nie auf Rechnung oder Angebot. Leer = kein Kontingent.
            </p>
            {deaktiviert && sperrHinweis && <p className="mt-0.5 text-[11px] text-slate-400">{sperrHinweis}</p>}
        </div>
    );
};
